import Transaction from '../../entities/transaction';
import {getCurrentFirmId, getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {genUUID} from '../../lib/picos/uuids';
// A namespace import: tests that mock these actions without this one still load the module
import * as TransactionActions from '../../store/actions/transactionActions';
import type {RootDispatch} from '../../@types/store';

/**
 * Portal charges (OWA-1454). The payments service charges a portal payment once per transactionId
 * and saves its ledger row itself, so the client never saves another version of that row.
 *
 * An attempt whose outcome is unknown keeps its transactionId and its original request until the
 * server settles it: retrying, reopening the payment dialog or reloading the page sends the same
 * request again, which the server answers from its record and never charges twice. A fresh
 * transactionId would be a new charge. The attempt is kept in localStorage, so it outlives the
 * tab, and a charge is never sent unless its attempt was stored first (no in-memory fallback, which
 * a reload or another tab would not see). A Web Lock per Case and transaction type lets one tab at
 * a time run an attempt; a click while another tab holds it sends nothing (it is never queued to run
 * afterwards, when it could start a second charge).
 */
export type PortalChargeRequest = {
    paymentMethodId: string,
    amount: number,
    accountId: string,
    // The transaction as sent, replayed byte for byte: the server refuses a retry whose intent differs
    transaction: Record<string, unknown>,
};

export type PortalChargeAttempt = {
    transactionId: string,
    request: PortalChargeRequest,
    // The server stopped reconciling this attempt (PORTAL_CHARGE_OPERATOR_REQUIRED): the firm checks it
    operatorRequired?: boolean,
};

function storageKey(matterId: string, txType: string): string {
    return `outlaw.payments.portalCharge:${getCurrentFirmId()}:${getCurrentUserId()}:${matterId}:${txType}`;
}

function isAttempt(value: unknown): value is PortalChargeAttempt {
    const v = value as PortalChargeAttempt;
    return !!v && typeof v.transactionId === 'string' && !!v.request && typeof v.request.paymentMethodId === 'string'
        && typeof v.request.amount === 'number' && !!v.request.transaction;
}

/**
 * The unsettled attempt to pay this Case with this transaction type, if there is one. Unreadable
 * storage reads as none; recording a new attempt then fails, so nothing is sent.
 */
export function pendingPortalCharge(matterId: string, txType: string): PortalChargeAttempt | null {
    try {
        const stored = localStorage.getItem(storageKey(matterId, txType));
        const parsed = stored ? JSON.parse(stored) : null;
        return isAttempt(parsed) ? parsed : null;
    }
    catch {
        return null;
    }
}

/**
 * Record an attempt before it is sent, so a lost answer, a reload or another tab retries the same
 * charge. Returns whether the record was stored and reads back as written; the caller sends nothing
 * when it was not.
 */
export function rememberPortalCharge(matterId: string, txType: string, attempt: PortalChargeAttempt): boolean {
    const name = storageKey(matterId, txType);
    const value = JSON.stringify(attempt);
    try {
        localStorage.setItem(name, value);
        return localStorage.getItem(name) === value;
    }
    catch {
        return false;
    }
}

/**
 * The server settled this attempt (charged or refused), so the next payment is a new charge. Only
 * the attempt with this transactionId is removed, never one another tab recorded since.
 */
export function forgetPortalCharge(matterId: string, txType: string, transactionId: string): void {
    if (pendingPortalCharge(matterId, txType)?.transactionId !== transactionId) return;
    try {
        localStorage.removeItem(storageKey(matterId, txType));
    }
    catch {
        // The attempt stays; retrying it answers from the server's record without charging again.
    }
}

/** Whether this browser can hold a portal charge's lock across tabs. */
export function portalChargeLocksAvailable(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.locks;
}

/**
 * Run one portal charge attempt for this Case and transaction type while holding a lock that every
 * tab of this browser shares, so two tabs never read "no attempt" and each start their own charge.
 * When another tab holds the lock the work does not run (ran is false). The caller checks
 * portalChargeLocksAvailable first.
 */
export async function withPortalChargeLock(matterId: string, txType: string, work: () => Promise<void>): Promise<{ran: boolean}> {
    return navigator.locks.request(storageKey(matterId, txType), {ifAvailable: true}, async lock => {
        if (!lock) return {ran: false};
        await work();
        return {ran: true};
    });
}

/**
 * The transactionId and amount a new payment dialog for this Case starts with: those of the
 * unsettled attempt when there is one, so reopening the dialog retries it rather than charging again.
 */
export function portalPaymentStart(matterId: string, txType: string, amount: number): {newId: string, amount: number} {
    const pending = pendingPortalCharge(matterId, txType);
    return pending ? {newId: pending.transactionId, amount: pending.request.amount} : {newId: genUUID(), amount};
}

// The server's rows for this tab's recent portal charges, so a dialog closing later adopts that row
const savedRows = new Map<string, Record<string, unknown>>();
const SAVED_ROWS_KEPT = 20;

/**
 * Put the ledger row the payments service saved for a portal charge into the store, as a read
 * would. Returns a fresh Transaction built from the row.
 */
export function adoptPortalTransaction(dispatch: RootDispatch, row: Record<string, unknown>): Transaction {
    const transaction = new Transaction(row);
    savedRows.delete(transaction.id);
    savedRows.set(transaction.id, row);
    // A Map iterates in insertion order, so the first key is the oldest
    if (savedRows.size > SAVED_ROWS_KEPT) savedRows.delete(savedRows.keys().next().value);
    dispatch(TransactionActions.fetchTransactionSuccess([new Transaction(row)], true, newHRTimestamp()));
    return transaction;
}

/**
 * The portal payment dialog closed with this transaction. Its charge, if any, already saved the
 * row: put that saved row in the store again, never the dialog's copy, whose version stamp the card
 * may have changed. Returns whether there was a saved row.
 */
export function adoptSavedPortalTransaction(dispatch: RootDispatch, transactionId: string | undefined): boolean {
    const row = transactionId ? savedRows.get(transactionId) : undefined;
    if (!row) return false;
    dispatch(TransactionActions.fetchTransactionSuccess([new Transaction(row)], true, newHRTimestamp()));
    return true;
}

/**
 * Read a portal payment's ledger row from the server and put it in the store, as a read would. The
 * portal never saves a payment row, so this is how it learns of a change: a declined charge's refused
 * row, or a refresh of a charge's status. A missing row (an explicit refusal saves none) is no error.
 */
export async function refreshPortalTransaction(dispatch: RootDispatch, transactionId: string): Promise<Transaction | null> {
    // Loaded here, not at the top: the REST layer is heavy, and the portal dialogs only need it after a charge
    const {findOne} = await import('../../rest/api');
    const row = await findOne<Transaction>('transaction', transactionId);
    if (!row) return null;
    return adoptPortalTransaction(dispatch, {...row});
}

/** What a portal client reads when the outcome of their payment is unknown. */
export function portalChargeUnknownMessage(): string {
    return 'Outlaw could not confirm whether your payment went through. Try again in a few minutes: ' +
        'Outlaw checks this same payment and does not charge you twice.';
}

/** What a portal client reads when the firm has to check their payment with the processor. */
export function portalChargeOperatorMessage(): string {
    return 'Outlaw could not confirm whether your payment went through, and the firm needs to check it with ' +
        'the payment processor. Contact the firm before you pay again.';
}

/**
 * What a portal client reads when a retried payment fails in a way only the firm can sort out (for
 * example the saved payment method is gone). The payment stays unconfirmed, so a new one is not offered.
 */
export function portalChargeStuckMessage(detail: string): string {
    return `Outlaw could not confirm your earlier payment (${detail}). Contact the firm before you pay again.`;
}

/** What a portal client reads when another payment for this Case is already being sent. */
export function portalChargeBusyMessage(): string {
    return 'Another payment for this Case is being sent, so Outlaw did not send this one. ' +
        'Wait for it to finish and check its result before you pay again.';
}

/** What a portal client reads when the firm's payment settings have not loaded yet, so nothing was sent. */
export function portalChargeFirmNotLoadedMessage(): string {
    return 'Outlaw could not load the firm\'s payment settings, so it did not send this payment. ' +
        'Reload the page and try again.';
}

/** What a portal client reads when the payment server did not accept their sign-in. */
export function portalChargeSignInMessage(): string {
    return 'Outlaw could not confirm this payment, possibly because your sign-in has expired. Sign in again and ' +
        'press Pay: Outlaw checks this same payment and does not charge you twice.';
}

/** What a portal client reads when the payment is not linked to a Case, which an online payment needs. */
export function portalChargeNoCaseMessage(): string {
    return 'This payment is not linked to a Case, so Outlaw cannot take it online. Contact the firm to pay.';
}

/** What a portal client reads when the browser could not keep a record of the payment, so it was not sent. */
export function portalChargeNoRecordMessage(): string {
    return 'Outlaw did not send this payment, because this browser would not let it keep a record of the payment. ' +
        'Allow this site to store data, or use another browser, and try again.';
}

/** What a portal client reads when the browser cannot coordinate payments between its tabs, so nothing was sent. */
export function portalChargeOldBrowserMessage(): string {
    return 'Outlaw did not send this payment, because this browser is too old to send it safely. ' +
        'Update the browser or use another one, and try again.';
}

/** What a portal client reads when the firm takes payments through a processor the portal cannot use. */
export function portalChargeUnavailableMessage(): string {
    return 'Online payment is not available for this firm. Contact the firm to pay.';
}

/** What a portal client reads when the processor refused their payment. */
export function portalChargeRefusedMessage(): string {
    return 'Your payment was declined. You can try again, for example with another payment method.';
}
